import React, { useCallback, useMemo } from 'react';
import { HStack, VStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Card } from '@televet/kibble-ui/build/components/Card';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { PetAvatar } from 'shared/components/Avatars';
import { AppointmentPetFragment } from 'shared/types/graphql';

interface AppointmentPetCardProps {
  pet: AppointmentPetFragment;
  handleDeletePet?: () => void;
  handleSelectPet?: (pet: AppointmentPetFragment) => void;
}

export const AppointmentPetCard = ({ pet, handleSelectPet, handleDeletePet }: AppointmentPetCardProps): JSX.Element => {
  const handleCardClick = useCallback(() => {
    if (!handleSelectPet) return;
    handleSelectPet(pet);
  }, [pet, handleSelectPet]);

  const handleDeleteClick = useCallback(() => {
    if (!handleDeletePet) return;
    handleDeletePet();
  }, [handleDeletePet]);

  const petParentDisplayNames = useMemo(() => {
    return pet.petParents.map((petParent) => `${petParent.firstName} ${petParent.lastName}`).join(', ');
  }, [pet.petParents]);

  return (
    <Card size="sm" onClick={handleCardClick} isInteractive key={pet.id} w="100%">
      <HStack
        className={`AppointmentModal__${handleDeletePet ? 'SelectedPet' : 'PetSearchResult'}`}
        w="100%"
        justify="space-between"
      >
        <HStack>
          <PetAvatar
            name={pet.name}
            speciesType={pet.speciesType}
            photoUrl={pet.photoUrl}
            isDeceased={pet.isDeceased || false}
            isActive={pet.isActive || true}
            size="lg"
          />
          <VStack spacing={0} align="flex-start">
            <Heading size="sm" data-testid="appointment-pet-name">
              {pet.name}
            </Heading>
            <Text as="p">{petParentDisplayNames}</Text>
            <Text size="sm" variant="subtle" as="p">
              {pet.breed || 'Breed Not Available'}
            </Text>
            <Text size="sm" variant="subtle" as="p">
              {pet.gender || 'Sex Not Available'}
            </Text>
          </VStack>
        </HStack>
        {!!handleDeletePet && <Button variant="ghostNeutral" iconName="trashcan" onClick={handleDeleteClick} />}
      </HStack>
    </Card>
  );
};
